import type { Rent, TenantImportRent, TenantImportRow } from '@einwert/valuation-api-client'
import { MonetaryValue } from 'ComponentLibrary/src/common/money'

export type PreviewTenant = NonNullable<TenantImportRow['prepared']>['tenant'] & { market_rent?: Rent }

function previewRent(rent: TenantImportRent | null): Rent {
    return {
        value: MonetaryValue.fromDecimal(rent?.amount ?? 0),
        payment_period: rent?.payment_period ?? 'monthly',
        payment_scope: rent?.payment_scope ?? 'absolute',
    }
}

export function toPreviewTenant(row: TenantImportRow): PreviewTenant {
    if (row.prepared) {
        return { ...row.prepared.tenant, market_rent: row.prepared.tenant_parameters?.reasonable_rent }
    }
    // Defaults are only for displaying incomplete drafts; saving uses prepared tenants.
    const agreement = {
        rent: previewRent(row.contract_rent),
        tenant_name: row.tenant_name ?? undefined,
        lease_number: row.lease_number ?? undefined,
        start_date: row.start_date ?? undefined,
        end_date: row.end_date ?? undefined,
        options: row.options ?? undefined,
        private_person: row.private_person,
        rent_free_periods: row.rent_free_periods,
    }
    let rentalAgreement: PreviewTenant['rental_agreement']
    if (row.rental_status !== 'unrented') {
        if (row.lease_type === 'graduated') {
            rentalAgreement = {
                ...agreement,
                rental_agreement_type: 'graduated',
                graduations: row.graduations.map((step) => ({ date: step.date, rent: previewRent(step.rent) })),
            }
        } else {
            rentalAgreement = { ...agreement, rental_agreement_type: row.lease_type ?? 'unknown' }
        }
    }
    return {
        market_rent: row.market_rent ? previewRent(row.market_rent) : undefined,
        reference_number: row.reference_number ?? undefined,
        usage_type: row.usage_type ?? 'other',
        rental_status: row.rental_status ?? 'rented',
        area: row.area_sqm === null ? undefined : { value: row.area_sqm, unit: 'sqm' },
        floor: row.floor ?? undefined,
        sub_usage_type: row.sub_usage_type,
        number_of_units: row.number_of_units ?? 0,
        note: row.note ?? undefined,
        vacancy_periods: row.vacancy_periods.map((period) => ({
            start_date: period.start_date ?? undefined,
            end_date: period.end_date ?? undefined,
            duration: period.duration ?? undefined,
        })),
        rental_agreement: rentalAgreement,
    }
}

export function previewYearlyRent(tenant: PreviewTenant, rent = tenant.rental_agreement?.rent): number {
    if (!rent) {
        return 0
    }
    const basis = { absolute: 1, per_area: tenant.area?.value ?? 0, per_unit: tenant.number_of_units }[rent.payment_scope]
    return MonetaryValue.fromMonetaryValueDataObject(rent.value)
        .multiply(basis)
        .multiply(rent.payment_period === 'monthly' ? 12 : 1).amount
}
